import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AspectRatio,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import {
  CATEGORIE_GIOCO,
  COMPETITIVITA,
  FASCE_ETA,
  LIVELLI,
  MATERIALI,
  MECCANICHE_GIOCO,
  OBIETTIVI_EDUCATIVI,
  SPAZI_GIOCO,
  TIPOLOGIE,
} from '../../constants/manualeGiocoFilters';
import {
  DEFAULT_SCHEMA_GRAZZINI,
  mergeSchemaGrazzini,
} from '../../constants/schemaGrazzini';
import {
  createManualeGiocoMine,
  uploadManualeGiocoMineFile,
} from '../../utils/manualeGiochiMemberApi';
import {
  numeroSquadreForPayload,
  numeroSquadreMaxForPayload,
} from '../../utils/manualeGiocoSquadre';
import ManualeGiocoSquadreField from './ManualeGiocoSquadreField';
import FormaCerchioPicker from './FormaCerchioPicker';
import ManualeGiocoFormSection from './ManualeGiocoFormSection';
import SchemaGrazziniEditor from './SchemaGrazziniEditor';

function buildMaterialeCheckboxOptions(presetList, selected) {
  const presetLower = new Set(presetList.map((m) => m.toLowerCase()));
  const extras = [];
  const seenExtra = new Set();
  (selected || []).forEach((m) => {
    const t = typeof m === 'string' ? m.trim() : '';
    if (!t) return;
    const low = t.toLowerCase();
    if (!presetLower.has(low) && !seenExtra.has(low)) {
      seenExtra.add(low);
      extras.push(t);
    }
  });
  extras.sort((a, b) => a.localeCompare(b, 'it'));
  return [...presetList, ...extras];
}

function canonicalMaterialeLabel(presetList, inputTrimmed) {
  const low = inputTrimmed.toLowerCase();
  const preset = presetList.find((m) => m.toLowerCase() === low);
  return preset ?? inputTrimmed;
}

const previewUrlFromUpload = (file) => {
  if (!file) return '';
  const fmt = file.formats || {};
  return (
    fmt.thumbnail?.url || fmt.small?.url || fmt.medium?.url || file.url || ''
  );
};

const numOrUndef = (v) => {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

function apiErrorMessage(err, fallback) {
  if (!err) return fallback;
  if (typeof err.message === 'string' && err.message) return err.message;
  if (err.data?.message) return err.data.message;
  return fallback;
}

/**
 * @param {{ onSuccess?: () => void }} props
 */
function ManualeGiocoForm({ onSuccess }) {
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');

  const [saving, setSaving] = useState(false);

  const [titolo, setTitolo] = useState('');
  const [descrizioneBreve, setDescrizioneBreve] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [competitivita, setCompetitivita] = useState('');
  const [formaCerchio, setFormaCerchio] = useState('');
  const [energia, setEnergia] = useState('');
  const [complessita, setComplessita] = useState('');
  const [rischio, setRischio] = useState('');
  const [numeroGiocatoriMin, setNumeroGiocatoriMin] = useState('');
  const [numeroGiocatoriMax, setNumeroGiocatoriMax] = useState('');
  const [etaMin, setEtaMin] = useState('');
  const [etaMax, setEtaMax] = useState('');
  const [numeroSquadre, setNumeroSquadre] = useState('');
  const [numeroSquadreMax, setNumeroSquadreMax] = useState('');
  const [durataMinuti, setDurataMinuti] = useState('');
  const [tempoPreparazioneMinuti, setTempoPreparazioneMinuti] = useState('');
  const [campoTestuale, setCampoTestuale] = useState('');

  const [regoleText, setRegoleText] = useState('');
  const [avvertenzeText, setAvvertenzeText] = useState('');
  const [variantiText, setVariantiText] = useState('');
  const [preparazioneText, setPreparazioneText] = useState('');

  const [categorieGioco, setCategorieGioco] = useState([]);
  const [meccanicheGioco, setMeccanicheGioco] = useState([]);
  const [fasceEta, setFasceEta] = useState([]);
  const [spaziGioco, setSpaziGioco] = useState([]);
  const [obiettiviEducativi, setObiettiviEducativi] = useState([]);
  const [materiali, setMateriali] = useState([]);
  const [materialeDraft, setMaterialeDraft] = useState('');

  const [extraImages, setExtraImages] = useState([]);
  const [extraDocs, setExtraDocs] = useState([]);
  const [schemaGrazzini, setSchemaGrazzini] = useState(() =>
    mergeSchemaGrazzini(DEFAULT_SCHEMA_GRAZZINI)
  );

  const materialeCheckboxOptions = useMemo(
    () => buildMaterialeCheckboxOptions(MATERIALI, materiali),
    [materiali]
  );

  const addMaterialeFromInput = useCallback(() => {
    const raw = materialeDraft.trim();
    if (!raw) {
      toast({ title: 'Inserisci un testo', status: 'info', duration: 2000 });
      return;
    }
    const canonical = canonicalMaterialeLabel(MATERIALI, raw);
    const low = canonical.toLowerCase();
    if (materiali.some((m) => String(m).trim().toLowerCase() === low)) {
      toast({ title: 'Già presente', status: 'warning', duration: 2500 });
      return;
    }
    setMateriali((prev) => [...prev, canonical]);
    setMaterialeDraft('');
  }, [materialeDraft, materiali, toast]);

  const addGalleryFiles = async (e) => {
    const files = [...(e.target.files || [])].filter((f) =>
      f.type.startsWith('image/')
    );
    e.target.value = '';
    const uploaded = await Promise.all(
      files.map(async (file) => {
        try {
          const data = await uploadManualeGiocoMineFile(file);
          const id = data.fileId ?? data.file?.id;
          const u = previewUrlFromUpload(data.file) || '';
          if (id != null) return { id, url: u, name: file.name };
        } catch (err) {
          toast({
            title: 'Upload immagine',
            description: apiErrorMessage(err, file.name),
            status: 'error',
            duration: 4000,
          });
        }
        return null;
      })
    );
    const next = uploaded.filter(Boolean);
    if (next.length) setExtraImages((prev) => [...prev, ...next]);
  };

  const addDocFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    const uploaded = await Promise.all(
      files.map(async (file) => {
        try {
          const data = await uploadManualeGiocoMineFile(file);
          const id = data.fileId ?? data.file?.id;
          if (id != null)
            return { id, url: data.file?.url || '', name: file.name };
        } catch (err) {
          toast({
            title: 'Upload documento',
            description: apiErrorMessage(err, file.name),
            status: 'error',
            duration: 4000,
          });
        }
        return null;
      })
    );
    const next = uploaded.filter(Boolean);
    if (next.length) setExtraDocs((prev) => [...prev, ...next]);
  };

  const buildPayload = () => {
    const data = {
      titolo: titolo.trim(),
      descrizioneBreve: descrizioneBreve.trim() || undefined,
      tipologia: tipologia || undefined,
      competitivita: competitivita || undefined,
      formaCerchio: formaCerchio || undefined,
      energia: energia || undefined,
      complessita: complessita || undefined,
      rischio: rischio || undefined,
      numeroGiocatoriMin: numOrUndef(numeroGiocatoriMin),
      numeroGiocatoriMax: numOrUndef(numeroGiocatoriMax),
      etaMin: numOrUndef(etaMin),
      etaMax: numOrUndef(etaMax),
      numeroSquadre: numeroSquadreForPayload(numeroSquadre),
      numeroSquadreMax: numeroSquadreMaxForPayload(
        numeroSquadre,
        numeroSquadreMax
      ),
      durataMinuti: numOrUndef(durataMinuti),
      tempoPreparazioneMinuti: numOrUndef(tempoPreparazioneMinuti),
      campoTestuale: campoTestuale.trim() || undefined,
      regoleText,
      avvertenzeText: avvertenzeText.trim() || undefined,
      variantiText: variantiText.trim() || undefined,
      preparazioneText: preparazioneText.trim() || undefined,
      categorieGioco,
      meccanicheGioco,
      fasceEta,
      spaziGioco,
      obiettiviEducativi,
      materiali,
      immagini: extraImages.map((x) => x.id),
      documenti: extraDocs.map((x) => x.id),
      schemaGrazzini: mergeSchemaGrazzini(schemaGrazzini),
    };
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titolo.trim()) {
      toast({
        title: 'Titolo obbligatorio',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!regoleText.trim()) {
      toast({
        title: 'Regole obbligatorie',
        description: 'Inserisci almeno un paragrafo nelle regole.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      await createManualeGiocoMine(buildPayload());
      toast({
        title: 'Bozza inviata',
        description:
          'La scheda è stata salvata come bozza. Verrà revisionata da CIL prima della pubblicazione.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      onSuccess?.();
    } catch (err) {
      toast({
        title: 'Errore salvataggio',
        description: apiErrorMessage(err, 'Controlla i campi e riprova.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const multiBlock = (label, options, value, onChange, hideLabel = false) => (
    <FormControl>
      {label && !hideLabel ? (
        <FormLabel fontSize="sm">{label}</FormLabel>
      ) : null}
      <CheckboxGroup value={value} onChange={onChange}>
        <Wrap spacing={2}>
          {options.map((opt) => (
            <Checkbox key={opt} value={opt} size="sm">
              {opt}
            </Checkbox>
          ))}
        </Wrap>
      </CheckboxGroup>
    </FormControl>
  );

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={cardBg}
      py={{ base: 4, md: 6 }}
      px={{ base: 2, sm: 3, md: 4 }}
      borderRadius="md"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <VStack align="stretch" spacing={6}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            La scheda viene salvata automaticamente come <strong>bozza</strong>{' '}
            e non sarà visibile nel manuale pubblico finché non verrà approvata
            da un amministratore di CIL.
          </AlertDescription>
        </Alert>

        <FormControl isRequired>
          <FormLabel>Titolo</FormLabel>
          <Input
            value={titolo}
            onChange={(ev) => setTitolo(ev.target.value)}
            placeholder="Nome del gioco"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Descrizione breve</FormLabel>
          <Textarea
            rows={3}
            value={descrizioneBreve}
            onChange={(ev) => setDescrizioneBreve(ev.target.value)}
          />
        </FormControl>

        <Divider />

        <ManualeGiocoFormSection title="Scheda tecnica" first>
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
            <FormControl>
              <FormLabel>Giocatori min</FormLabel>
              <Input
                type="number"
                min={0}
                value={numeroGiocatoriMin}
                onChange={(ev) => setNumeroGiocatoriMin(ev.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Giocatori max</FormLabel>
              <Input
                type="number"
                min={0}
                value={numeroGiocatoriMax}
                onChange={(ev) => setNumeroGiocatoriMax(ev.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Durata (min)</FormLabel>
              <Input
                type="number"
                min={0}
                value={durataMinuti}
                onChange={(ev) => setDurataMinuti(ev.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Prep. (min)</FormLabel>
              <Input
                type="number"
                min={0}
                value={tempoPreparazioneMinuti}
                onChange={(ev) => setTempoPreparazioneMinuti(ev.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Età min</FormLabel>
              <Input
                type="number"
                min={0}
                value={etaMin}
                onChange={(ev) => setEtaMin(ev.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Età max</FormLabel>
              <Input
                type="number"
                min={0}
                value={etaMax}
                onChange={(ev) => setEtaMax(ev.target.value)}
              />
            </FormControl>
          </SimpleGrid>
          <ManualeGiocoSquadreField
            numeroSquadre={numeroSquadre}
            onNumeroSquadreChange={setNumeroSquadre}
            numeroSquadreMax={numeroSquadreMax}
            onNumeroSquadreMaxChange={setNumeroSquadreMax}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Livelli e intensità">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <FormControl>
              <FormLabel>Tipologia</FormLabel>
              <Select
                placeholder="—"
                value={tipologia}
                onChange={(ev) => setTipologia(ev.target.value)}
              >
                {TIPOLOGIE.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Competitività</FormLabel>
              <Select
                placeholder="—"
                value={competitivita}
                onChange={(ev) => setCompetitivita(ev.target.value)}
              >
                {COMPETITIVITA.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <FormControl>
              <FormLabel>Energia</FormLabel>
              <Select
                placeholder="—"
                value={energia}
                onChange={(ev) => setEnergia(ev.target.value)}
              >
                {LIVELLI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Complessità</FormLabel>
              <Select
                placeholder="—"
                value={complessita}
                onChange={(ev) => setComplessita(ev.target.value)}
              >
                {LIVELLI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Rischio</FormLabel>
              <Select
                placeholder="—"
                value={rischio}
                onChange={(ev) => setRischio(ev.target.value)}
              >
                {LIVELLI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Forma del cerchio">
          <FormaCerchioPicker
            value={formaCerchio}
            onChange={setFormaCerchio}
            hideLabel
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Materiali">
          <FormControl>
            <CheckboxGroup value={materiali} onChange={setMateriali}>
              <Wrap spacing={2}>
                {materialeCheckboxOptions.map((opt) => (
                  <Checkbox key={opt} value={opt} size="sm">
                    {opt}
                  </Checkbox>
                ))}
              </Wrap>
            </CheckboxGroup>
            <HStack mt={3} spacing={2}>
              <Input
                size="sm"
                placeholder="Altro materiale…"
                value={materialeDraft}
                onChange={(ev) => setMaterialeDraft(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    addMaterialeFromInput();
                  }
                }}
              />
              <Button
                size="sm"
                leftIcon={<FaPlus />}
                onClick={addMaterialeFromInput}
              >
                Aggiungi
              </Button>
            </HStack>
            <FormHelperText>
              Se il tuo socio è collegato al profilo, verrai indicato come
              autore automaticamente.
            </FormHelperText>
          </FormControl>
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Campo e spazio">
          <Textarea
            rows={2}
            value={campoTestuale}
            onChange={(ev) => setCampoTestuale(ev.target.value)}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Preparazione">
          <Textarea
            rows={4}
            value={preparazioneText}
            onChange={(ev) => setPreparazioneText(ev.target.value)}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Regole">
          <FormControl isRequired>
            <Textarea
              rows={8}
              value={regoleText}
              onChange={(ev) => setRegoleText(ev.target.value)}
            />
          </FormControl>
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Varianti">
          <Textarea
            rows={4}
            value={variantiText}
            onChange={(ev) => setVariantiText(ev.target.value)}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Avvertenze">
          <Textarea
            rows={4}
            value={avvertenzeText}
            onChange={(ev) => setAvvertenzeText(ev.target.value)}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Categorie">
          {multiBlock(
            '',
            CATEGORIE_GIOCO,
            categorieGioco,
            setCategorieGioco,
            true
          )}
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Meccaniche">
          {multiBlock(
            '',
            MECCANICHE_GIOCO,
            meccanicheGioco,
            setMeccanicheGioco,
            true
          )}
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Fasce d'età">
          {multiBlock('', FASCE_ETA, fasceEta, setFasceEta, true)}
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Spazi">
          {multiBlock('', SPAZI_GIOCO, spaziGioco, setSpaziGioco, true)}
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Obiettivi educativi">
          {multiBlock(
            '',
            OBIETTIVI_EDUCATIVI,
            obiettiviEducativi,
            setObiettiviEducativi,
            true
          )}
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Schema di Grazzini-Hoffmann e Staccioli">
          <SchemaGrazziniEditor
            value={schemaGrazzini}
            onChange={setSchemaGrazzini}
          />
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Documenti">
          <VStack align="stretch" spacing={1}>
            {extraDocs.map((d) => (
              <HStack key={d.id} justify="space-between">
                <Text fontSize="sm" noOfLines={1}>
                  {d.name}
                </Text>
                <IconButton
                  size="xs"
                  aria-label="Rimuovi"
                  icon={<FaTrash />}
                  onClick={() =>
                    setExtraDocs((prev) => prev.filter((x) => x.id !== d.id))
                  }
                />
              </HStack>
            ))}
            <Button
              as="label"
              htmlFor="mg-docs-client"
              size="sm"
              leftIcon={<FaPlus />}
              variant="outline"
              w="fit-content"
            >
              Carica file
            </Button>
            <Input
              id="mg-docs-client"
              type="file"
              multiple
              display="none"
              onChange={addDocFiles}
            />
          </VStack>
        </ManualeGiocoFormSection>

        <ManualeGiocoFormSection title="Galleria">
          <HStack wrap="wrap" spacing={2} align="flex-start">
            {extraImages.map((im) => (
              <Box key={im.id} position="relative" w="80px">
                <AspectRatio ratio={1}>
                  <Image
                    src={im.url}
                    alt=""
                    objectFit="cover"
                    borderRadius="md"
                  />
                </AspectRatio>
                <IconButton
                  size="xs"
                  position="absolute"
                  top={0}
                  right={0}
                  colorScheme="red"
                  aria-label="Rimuovi"
                  icon={<FaTrash />}
                  onClick={() =>
                    setExtraImages((prev) => prev.filter((x) => x.id !== im.id))
                  }
                />
              </Box>
            ))}
            <Button
              as="label"
              htmlFor="mg-gallery-client"
              size="sm"
              leftIcon={<FaPlus />}
              variant="outline"
            >
              Aggiungi
            </Button>
            <Input
              id="mg-gallery-client"
              type="file"
              accept="image/*"
              multiple
              display="none"
              onChange={addGalleryFiles}
            />
          </HStack>
        </ManualeGiocoFormSection>

        <Button
          type="submit"
          colorScheme="teal"
          size="lg"
          isLoading={saving}
          loadingText="Salvataggio bozza…"
        >
          Invia bozza
        </Button>
      </VStack>
    </Box>
  );
}

export default ManualeGiocoForm;
